import React from 'react';
import { Box as MuiBox, BoxProps as MuiBoxProps } from '@mui/material';
import {
  DataAttributeProps,
  PounceStyleProps,
  extractPounceProps,
  mapPouncePropsToSx,
} from '../../utils';

/**
 * Native attributes for the element type, excluding pounce props
 * Matches the pattern from original pounce Box
 */
export type NativeAttributes<El extends React.ElementType> = Omit<
  React.ComponentPropsWithRef<El> & MuiBoxProps,
  keyof PounceStyleProps | 'sx'
>;

/**
 * Box props that combine native element attributes with pounce styling props
 * Generic type parameter T determines which element's attributes are included
 */
export type BoxProps<T extends React.ElementType = 'div'> = NativeAttributes<T> &
  PounceStyleProps &
  DataAttributeProps & {
    /** The HTML element or React component to render as the Box */
    as?: T;

    /** Additional styles to apply (combined with pounce props) */
    sx?: MuiBoxProps['sx'];
  };

/**
 * Box component with full pounce API compatibility.
 *
 * Provides the same API as the original pounce Box component, allowing for easy migration
 * from the original pounce library. All pounce-style props are automatically converted
 * to MUI sx props while maintaining theme integration.
 *
 * Features:
 * - Full pounce API compatibility (spacing, colors, typography, layout, etc.)
 * - Automatic theme value resolution (e.g., backgroundColor="blue-400")
 * - All MUI Box capabilities through sx prop
 * - Polymorphic component support with 'as' prop
 * - Type-safe element props based on 'as' prop (e.g., xmlns for svg, href for a, onClick for button)
 */
export const Box = React.forwardRef(function Box<T extends React.ElementType = 'div'>(
  { as, children, sx, ...rest }: BoxProps<T>,
  ref: React.ForwardedRef<React.ElementRef<T>>,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user-provided sx
  // MUI sx prop supports object merging when both are objects
  const combinedSx = { ...pounceStyles, ...sx };

  return (
    <MuiBox ref={ref} component={as || 'div'} sx={combinedSx} {...otherProps}>
      {children}
    </MuiBox>
  );
}) as <T extends React.ElementType = 'div'>(
  props: BoxProps<T> & { ref?: React.ForwardedRef<React.ElementRef<T>> },
) => React.ReactElement;
